import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { formatDateYYYYMMDD, type Locale } from "@/shared/utils";
import type {
  Form100Data,
  Form10001Data,
  Form10002Data,
  Form10007Data,
  Form10010Response,
  Form10011Response,
  Form20000Response,
  Form20001Response,
  Form20005Response,
  TaxpayerMeta,
} from "../types/api";
import { useGetCitReportQuery, useGetPayrollTaxReportQuery, useGetCitForm10007Query, useGetCitForm10010Query, useGetCitForm10011Query } from "../api";
import type { TaxType } from "../consts";
import { DEFAULT_CIT_FORM, DEFAULT_PAYROLL_FORM } from "../consts";

export interface UseTaxPageReturn {
  taxType: TaxType;
  setTaxType: (type: TaxType) => void;
  year: number | null;
  setYear: (year: number | null) => void;
  quarter: number | null;
  setQuarter: (quarter: number | null) => void;
  endDate: Date | null;
  setEndDate: (date: Date | null) => void;
  includeGph: boolean;
  setIncludeGph: (include: boolean) => void;
  activeForm: string;
  setActiveForm: (form: string) => void;
  isReportGenerated: { cit: boolean; payroll: boolean };
  generateReport: (type: TaxType) => void;

  form100Data: Form100Data | undefined;
  form100Loading: boolean;
  form100Error: boolean;
  form10001Data: Form10001Data | undefined;
  form10001Loading: boolean;
  form10001Error: boolean;
  form10002Data: Form10002Data | undefined;
  form10002Loading: boolean;
  form10002Error: boolean;
  form10007Data: Form10007Data | undefined;
  form10007Loading: boolean;
  form10007Error: boolean;
  form10010Data: Form10010Response | undefined;
  form10010Loading: boolean;
  form10010Error: boolean;
  form10011Data: Form10011Response | undefined;
  form10011Loading: boolean;
  form10011Error: boolean;
  projectId: number | null;
  setProjectId: (id: number | null) => void;

  form200Data: Form20000Response | undefined;
  form200Loading: boolean;
  form200Error: boolean;
  form20001Data: Form20001Response | undefined;
  form20001Loading: boolean;
  form20001Error: boolean;
  form20005Data: Form20005Response | undefined;
  form20005Loading: boolean;
  form20005Error: boolean;

  taxpayerMeta: TaxpayerMeta | undefined;
  form10007ExportData: Form10007Data | undefined;

  locale: Locale;
  t: ReturnType<typeof useTranslation>["t"];
}

export function useTaxPage(): UseTaxPageReturn {
  const { i18n, t } = useTranslation("TaxPage");
  const locale = i18n.language as Locale;

  const [taxType, setTaxType] = useState<TaxType>("cit");
  const [year, setYear] = useState<number | null>(new Date().getFullYear());
  const [quarter, setQuarter] = useState<number | null>(1);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [includeGph, setIncludeGph] = useState<boolean>(false);
  const [activeForm, setActiveForm] = useState<string>(DEFAULT_CIT_FORM);
  const [projectId, setProjectId] = useState<number | null>(null);
  const [isReportGenerated, setIsReportGenerated] = useState<{ cit: boolean; payroll: boolean }>({
    cit: false,
    payroll: false,
  });

  useEffect(() => {
    if (taxType === "cit") {
      setActiveForm(DEFAULT_CIT_FORM);
    } else {
      setActiveForm(DEFAULT_PAYROLL_FORM);
    }
  }, [taxType]);

  useEffect(() => {
    setIsReportGenerated((prev) => ({ ...prev, cit: false }));
  }, [year, endDate]);

  useEffect(() => {
    setIsReportGenerated((prev) => ({ ...prev, payroll: false }));
  }, [year, quarter, includeGph]);

  function generateReport(type: TaxType) {
    setIsReportGenerated((prev) => ({ ...prev, [type]: true }));
  }

  const citParams = useMemo(() => {
    if (!year) return null;
    return {
      year,
      ...(endDate && { end_date: formatDateYYYYMMDD(endDate) }),
    };
  }, [year, endDate]);

  const payrollParams = useMemo(() => {
    if (!year || !quarter) return null;
    return {
      year,
      quarter,
      ...(includeGph && { include_gph: includeGph }),
    };
  }, [year, quarter, includeGph]);

  const form10007Params = useMemo(() => {
    if (!year) return null;
    return {
      year,
      ...(endDate && { as_of_date: formatDateYYYYMMDD(endDate) }),
    };
  }, [year, endDate]);

  const form10010Params = useMemo(() => {
    if (!year) return null;
    return {
      start_date: `${year}-01-01`,
      end_date: endDate ? formatDateYYYYMMDD(endDate) : `${year}-12-31`,
      ...(projectId && { project_id: projectId }),
    };
  }, [year, endDate, projectId]);

  const citReportQuery = useGetCitReportQuery(citParams || { year: 0 }, {
    skip: !citParams || !isReportGenerated.cit,
  });

  const form10007Query = useGetCitForm10007Query(
    form10007Params || { year: 0 },
    { skip: !form10007Params || !isReportGenerated.cit || activeForm !== "100.07" },
  );

  const form10010Query = useGetCitForm10010Query(
    form10010Params || { start_date: "", end_date: "" },
    { skip: !form10010Params || !isReportGenerated.cit || activeForm !== "100.10" },
  );

  const form10011Query = useGetCitForm10011Query(
    form10010Params || { start_date: "", end_date: "" },
    { skip: !form10010Params || !isReportGenerated.cit || activeForm !== "100.11" },
  );

  const payrollReportQuery = useGetPayrollTaxReportQuery(payrollParams || { year: 0, quarter: 1 }, {
    skip: !payrollParams || !isReportGenerated.payroll,
  });

  return {
    taxType,
    setTaxType,
    year,
    setYear,
    quarter,
    setQuarter,
    endDate,
    setEndDate,
    includeGph,
    setIncludeGph,
    activeForm,
    setActiveForm,
    projectId,
    setProjectId,
    isReportGenerated,
    generateReport,

    form100Data: citReportQuery.data?.form_100,
    form100Loading: citReportQuery.isLoading,
    form100Error: citReportQuery.isError,
    form10001Data: citReportQuery.data?.form_100_01,
    form10001Loading: citReportQuery.isLoading,
    form10001Error: citReportQuery.isError,
    form10002Data: citReportQuery.data?.form_100_02,
    form10002Loading: citReportQuery.isLoading,
    form10002Error: citReportQuery.isError,
    form10007Data: form10007Query.data,
    form10007Loading: form10007Query.isLoading,
    form10007Error: form10007Query.isError,
    form10010Data: form10010Query.data,
    form10010Loading: form10010Query.isLoading,
    form10010Error: form10010Query.isError,
    form10011Data: form10011Query.data,
    form10011Loading: form10011Query.isLoading,
    form10011Error: form10011Query.isError,

    form200Data: payrollReportQuery.data?.form_200,
    form200Loading: payrollReportQuery.isLoading,
    form200Error: payrollReportQuery.isError,
    form20001Data: payrollReportQuery.data?.form_200_01,
    form20001Loading: payrollReportQuery.isLoading,
    form20001Error: payrollReportQuery.isError,
    form20005Data: payrollReportQuery.data?.form_200_05,
    form20005Loading: payrollReportQuery.isLoading,
    form20005Error: payrollReportQuery.isError,

    taxpayerMeta:
      taxType === "cit" ? citReportQuery.data?.taxpayer_meta : payrollReportQuery.data?.taxpayer_meta,
    form10007ExportData: citReportQuery.data?.form_100_07 ?? form10007Query.data,

    locale,
    t,
  };
}
